import { supabase } from '../lib/supabase';
import { HistoricoMotoristaVeiculo, TrocaMotoristaPayload } from '../types/veiculoHistorico';

const STORAGE_KEY_PREFIX = 'frota_historico_motorista_';

export function normalizarPlaca(placa: string): string {
  return (placa || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function formatarPlaca(placa: string): string {
  const norm = normalizarPlaca(placa);
  if (!norm) return '';
  if (/^[A-Z]{3}[0-9]{4}$/.test(norm)) {
    return `${norm.slice(0, 3)}-${norm.slice(3)}`;
  }
  if (/^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(norm)) {
    return `${norm.slice(0, 3)}-${norm.slice(3)}`;
  }
  return placa.toUpperCase().trim();
}

/**
 * Lê o histórico salvo em `system_preference`, com queda para o localStorage.
 *
 * A tabela só possui as colunas `id` e `preference`. Além disso o supabase-js
 * devolve o erro em `{ error }` em vez de lançar exceção — por isso o fallback
 * precisa checar `error` explicitamente, e não depender de um try/catch.
 */
async function lerHistoricoSalvo(chave: string): Promise<HistoricoMotoristaVeiculo[]> {
  try {
    const { data, error } = await supabase
      .from('system_preference')
      .select('preference')
      .eq('id', chave)
      .maybeSingle();

    if (!error && data?.preference) {
      const parsed = JSON.parse(data.preference);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('[historicoMotorista] Erro ao ler do banco, usando cache local:', e);
  }

  try {
    const local = localStorage.getItem(chave);
    if (local) {
      const parsed = JSON.parse(local);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (_) {}

  return [];
}

export const veiculoHistoricoMotoristaService = {
  /**
   * Obtém o histórico consolidado de motoristas de um veículo:
   * 1. Registros manuais/automáticos de trocas de condutor salvos
   * 2. Ordens de Serviço reais executadas no banco por placa / ID
   */
  async getHistoricoPorVeiculo(veiculoId: string, placa?: string): Promise<HistoricoMotoristaVeiculo[]> {
    try {
      const placaFormatada = (placa || '').toUpperCase().trim();
      const placaNorm = normalizarPlaca(placaFormatada);
      
      // 1. Carrega os registros manuais/automáticos salvos na preferência do sistema
      const chaveStorage = `${STORAGE_KEY_PREFIX}${placaNorm || placaFormatada || veiculoId}`;
      const historicoSalvo = await lerHistoricoSalvo(chaveStorage);

      // 2. Extrai histórico real a partir das Ordens de Serviço antigas no Supabase
      // Isso reconstrói o passado mesmo para viagens de anos atrás!
      let osHistoricoMap = new Map<number, { count: number; minDate: string; maxDate: string }>();
      
      try {
        // Busca todos os IDs de veículos que compartilham a mesma placa (caso haja duplicatas antigas)
        let veiculoIds: string[] = [veiculoId];
        if (placaNorm) {
          const { data: veiculosMesmaPlaca } = await supabase
            .from('veiculos')
            .select('id, placa');
          if (veiculosMesmaPlaca && veiculosMesmaPlaca.length > 0) {
            const idsMesmaPlaca = veiculosMesmaPlaca
              .filter(v => normalizarPlaca(v.placa) === placaNorm)
              .map(v => String(v.id));
            veiculoIds = Array.from(new Set([...veiculoIds, ...idsMesmaPlaca]));
          }
        }

        const { data: osList } = await supabase
          .from('ordem_de_servico')
          .select('id, data_atendimento, agregado, veiculos_id')
          .in('veiculos_id', veiculoIds);

        if (osList && osList.length > 0) {
          for (const os of osList) {
            const motId = Number(os.agregado);
            if (motId && !isNaN(motId)) {
              const current = osHistoricoMap.get(motId) || { count: 0, minDate: os.data_atendimento || '', maxDate: os.data_atendimento || '' };
              current.count += 1;
              if (os.data_atendimento) {
                if (!current.minDate || os.data_atendimento < current.minDate) current.minDate = os.data_atendimento;
                if (!current.maxDate || os.data_atendimento > current.maxDate) current.maxDate = os.data_atendimento;
              }
              osHistoricoMap.set(motId, current);
            }
          }
        }
      } catch (osErr) {
        console.warn('Aviso ao consultar OS históricas do veículo:', osErr);
      }

      // 3. Carrega os nomes de todos os motoristas envolvidos
      const motoristaIds = Array.from(new Set([
        ...historicoSalvo.map(h => h.motorista_id).filter(Boolean),
        ...Array.from(osHistoricoMap.keys())
      ])) as number[];

      let nomesMap = new Map<number, string>();
      if (motoristaIds.length > 0) {
        const { data: pessoas } = await supabase
          .from('pessoa')
          .select('id, nome')
          .in('id', motoristaIds);
        if (pessoas) {
          pessoas.forEach(p => nomesMap.set(p.id, p.nome));
        }
      }

      // 4. Mescla o histórico registrado com as viagens encontradas no banco
      const resultadoMap = new Map<string, HistoricoMotoristaVeiculo>();

      historicoSalvo.forEach(item => {
        const key = `${item.motorista_id}_${item.data_inicio}`;
        const osInfo = item.motorista_id ? osHistoricoMap.get(item.motorista_id) : undefined;
        resultadoMap.set(key, {
          ...item,
          motorista_nome: item.motorista_id ? (nomesMap.get(item.motorista_id) || item.motorista_nome) : item.motorista_nome,
          total_os: (item.total_os || 0) + (osInfo?.count || 0),
          primeira_os_data: osInfo?.minDate || item.primeira_os_data || null,
          ultima_os_data: osInfo?.maxDate || item.ultima_os_data || null,
        });
      });

      // Adiciona motoristas que fizeram OS antigas mas ainda não tinham registro formal de troca
      osHistoricoMap.forEach((osInfo, motId) => {
        const jaExiste = Array.from(resultadoMap.values()).some(h => h.motorista_id === motId);
        if (!jaExiste) {
          const key = `auto_${motId}`;
          resultadoMap.set(key, {
            id: key,
            veiculo_id: veiculoId,
            placa: placaFormatada,
            motorista_id: motId,
            motorista_nome: nomesMap.get(motId) || `Motorista #${motId}`,
            data_inicio: osInfo.minDate || 'Histórico anterior',
            data_fim: osInfo.maxDate || null,
            motivo: 'Identificado pelas Ordens de Serviço executadas',
            is_atual: false,
            total_os: osInfo.count,
            primeira_os_data: osInfo.minDate,
            ultima_os_data: osInfo.maxDate,
            created_at: new Date().toISOString(),
          });
        }
      });

      const listaFinal = Array.from(resultadoMap.values());
      // Ordena: motorista atual primeiro, depois do mais recente para o mais antigo
      return listaFinal.sort((a, b) => {
        if (a.is_atual && !b.is_atual) return -1;
        if (!a.is_atual && b.is_atual) return 1;
        return (b.data_inicio || '').localeCompare(a.data_inicio || '');
      });
    } catch (err) {
      console.error('Erro ao buscar histórico de motoristas do veículo:', err);
      return [];
    }
  },

  /**
   * Registra uma nova troca de motorista para o veículo.
   * Finaliza o período do condutor anterior e define o novo como ativo.
   */
  async registrarTrocaMotorista(payload: TrocaMotoristaPayload): Promise<void> {
    const placaFormatada = (payload.placa || '').toUpperCase().trim();
    // A chave precisa usar a placa normalizada (sem traço), igual à leitura em
    // getHistoricoPorVeiculo — senão grava em "..._ABC-1234" e lê "..._ABC1234".
    const placaNorm = normalizarPlaca(placaFormatada);
    const chaveStorage = `${STORAGE_KEY_PREFIX}${placaNorm || placaFormatada || payload.veiculo_id}`;
    const dataTroca = payload.data_troca || new Date().toISOString().split('T')[0];

    // Carrega histórico atual
    let historico: HistoricoMotoristaVeiculo[] = await lerHistoricoSalvo(chaveStorage);

    // Marca motoristas anteriores como não ativos e fecha a data_fim
    historico = historico.map(h => {
      if (h.is_atual) {
        return {
          ...h,
          is_atual: false,
          data_fim: h.data_fim || dataTroca,
        };
      }
      return h;
    });

    // Adiciona o novo motorista
    if (payload.novo_motorista_id || payload.novo_motorista_nome) {
      const novoRegistro: HistoricoMotoristaVeiculo = {
        id: `troca_${Date.now()}`,
        veiculo_id: payload.veiculo_id,
        placa: placaFormatada,
        motorista_id: payload.novo_motorista_id,
        motorista_nome: payload.novo_motorista_nome,
        data_inicio: dataTroca,
        data_fim: null,
        motivo: payload.motivo || 'Assumiu o veículo',
        is_atual: true,
        created_at: new Date().toISOString(),
      };
      historico.unshift(novoRegistro);
    }

    const jsonStr = JSON.stringify(historico);

    // Salva no Supabase (system_preference) com fallback para localStorage
    localStorage.setItem(chaveStorage, jsonStr);

    const { error } = await supabase
      .from('system_preference')
      .upsert({ id: chaveStorage, preference: jsonStr });

    if (error) {
      console.warn('Histórico salvo apenas no cache local — erro no Supabase:', error.message);
    }
  },

  /**
   * Analisa e agrupa veículos duplicados por placa (normalizada sem traços ou espaços)
   */
  identificarPlacasDuplicadas(veiculos: any[]): Map<string, any[]> {
    const mapa = new Map<string, any[]>();
    veiculos.forEach(v => {
      const placaNorm = normalizarPlaca(v.placa || '');
      if (placaNorm && placaNorm !== 'SEMPLACA' && placaNorm !== 'SEMVEICULO' && placaNorm !== 'SEMPLACA') {
        const list = mapa.get(placaNorm) || [];
        list.push(v);
        mapa.set(placaNorm, list);
      }
    });

    // Retorna apenas as placas com mais de 1 cadastro
    const duplicadas = new Map<string, any[]>();
    mapa.forEach((lista, placaNorm) => {
      if (lista.length > 1) {
        const placaFormatada = lista.find(v => (v.placa || '').includes('-'))?.placa || formatarPlaca(lista[0].placa || placaNorm);
        duplicadas.set(placaFormatada, lista);
      }
    });
    return duplicadas;
  }
};
